import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import uuid from 'node-uuid';
import { head, map, get } from 'lodash';
import { compose } from 'redux';
import { payeePropTypes } from '../../../sharedComponents/PayeeList/propTypes';
import withContent from '../../../containers/withContent';
import { withScrollTop } from '../../../containers/withScrollTop';
import pageContent from '../CreditLandingPage/Credit.content';
import { getPayees } from '../../../states/payees/selectors';
import { goToPaymentsPage, setBackPage } from '../../../states/page/actions';

import { getReference, hasPreviousCopCheckPerformed, hasAllRequiredCopFields } from '../../../helpers/fieldHelpers';
import { clearSelectedPayment } from '../../../states/payeeSelector/actions';
import PayeesPage from '../../../sharedComponents/PayeesPage/PayeesPage';
import { getSelectedPaymentId } from '../../../states/payeeSelector/selectors';
import {
	addMultiplePayments,
	removePayment,
	updateFromAccount,
	updateToAccount,
	updateReference,
	updateHmrcRef,
} from '../../../states/bacsCredit/actions';

import {
	getPaymentCount,
	getToAccount,
	getBacsPaymentName,
	getFromAccount,
} from '../../../states/bacsCredit/selectors';
import { getCurrentTemplateId, getIsTemplateMode } from '../../../states/templates/selectors';
import { isAuthorisationEdit } from '../../../states/modes/selectors';
import { CAPABILITIES } from '../../../utils/accountUtils';
import { BACS_CREDIT_CODE, PAYMENTS_LIMIT } from '../../../config';
import { getAccountsForBacsOnType } from '../../../states/accounts/selectors';
import { getPayeeById } from '../../../states/commonSelectors/selectors';
import { goToEditPayeePage } from '../../../states/api/fasterPaymentActions';
import { setSelectedPayee } from '../../../managePayee/states/action';
import { getPage } from '../../../states/page/selectors';
import { COPCheck } from '../../../sharedComponents/COPCheck/COPCheck';
import { getLastPayment } from '../../../states/payments/selectors';

const mapState = (state) => ({
	toPayees: getPayees(state),
	paymentCount: getPaymentCount(state),
	toAccount: getToAccount(state),
	selectedPaymentId: getSelectedPaymentId(state),
	isAuthorisationEdit: isAuthorisationEdit(state),
	templateMode: getIsTemplateMode(state),
	templateId: getCurrentTemplateId(state),
	paymentName: getBacsPaymentName(state),
	fromAccount: getFromAccount(state),
	fromAccounts: getAccountsForBacsOnType(state, BACS_CREDIT_CODE),
	getPayeeById: (id) => getPayeeById(state, id),
	currentPage: getPage(state),
	lastPayment: getLastPayment(state),
});

const mapDispatch = (dispatch) => ({
	clearSelectedPayment: () => dispatch(clearSelectedPayment()),
	addMultiplePayments: (payments) => dispatch(addMultiplePayments(payments)),
	continueToPayments: () => dispatch(goToPaymentsPage()),
	handleRemovePayment: (id) => dispatch(removePayment({ id })),
	handleSelectToAccount: (id, accountId, toPayees) => {
		dispatch(updateToAccount({ id, accountId }));
		dispatch(updateReference({ id, reference: getReference(toPayees, accountId) }));
		dispatch(updateHmrcRef({ id, hmrcRef: '' }));
	},
	updateFromAccount: ({ accountId }) => dispatch(updateFromAccount({ accountId })),
	goToEditPayeePage: (isCOPEdit) => dispatch(goToEditPayeePage(isCOPEdit)),
	setSelectedPayee: (payee) => dispatch(setSelectedPayee(payee)),
	setBackPage: (page) => dispatch(setBackPage(page)),
});

class BacsPayeePage extends Component {
	constructor(props) {
		super(props);

		this.handlePayeeListContinue = this.handlePayeeListContinue.bind(this);
		this.handleCOPSuccess = this.handleCOPSuccess.bind(this);
		this.handleCOPCancel = this.handleCOPCancel.bind(this);
		this.handleEditPayee = this.handleEditPayee.bind(this);

		this.state = { isCOPCheckRequired: false, payeeId: '' };
	}

	handleEditPayee(selectedPayee, isCOPEdit = false) {
		const { setSelectedPayee, goToEditPayeePage, currentPage, setBackPage } = this.props;
		setSelectedPayee(selectedPayee);
		setBackPage(currentPage);
		goToEditPayeePage(isCOPEdit);
	}

	handlePayeeListContinue(selectedToPayees) {
		const {
			addMultiplePayments,
			continueToPayments,
			toPayees,
			handleSelectToAccount,
			selectedPaymentId,
			clearSelectedPayment,
			getPayeeById,
		} = this.props;
		if (selectedPaymentId) {
			handleSelectToAccount(selectedPaymentId, head(selectedToPayees), toPayees);
		} else {
			const multiplePayments = map(selectedToPayees, (toPayee) => ({
				id: uuid.v1(),
				toAccount: toPayee,
				reference: getReference(toPayees, toPayee),
			}));

			addMultiplePayments(multiplePayments);
		}

		if (selectedToPayees.length === 1) {
			const payee = getPayeeById(selectedToPayees[0]);
			if (!hasPreviousCopCheckPerformed(payee)) {
				if (hasAllRequiredCopFields(payee)) {
					this.setState({ isCOPCheckRequired: true, payeeId: selectedToPayees[0] });
				} else {
					this.handleEditPayee(payee, true);
				}
				return;
			}
		}

		clearSelectedPayment();
		return continueToPayments();
	}

	handleCOPSuccess() {
		this.props.continueToPayments();
	}

	handleCOPCancel() {
		const {
			lastPayment: { id: paymentId },
			handleRemovePayment,
			selectedPaymentId: isPaymentBeingEdited,
			setSelectedPayee,
		} = this.props;

		if (isPaymentBeingEdited) {
			setSelectedPayee(null);
		} else if (paymentId) {
			handleRemovePayment(paymentId);
		} else {
			handleRemovePayment(get(this.props, 'payments[0].id'));
		}
		this.setState({ isCOPCheckRequired: false });
	}

	handleCOPEdit = (payeeId) => {
		const payee = this.props.getPayeeById(payeeId);
		this.handleEditPayee(payee, true);
	};

	render() {
		const {
			toPayees,
			content,
			copCheckTriggered,
			paymentCount,
			toAccount,
			updateFromAccount,
			templateMode,
			templateId,
			paymentName,
			isAuthorisationEdit,
			fromAccount,
			fromAccounts,
			paymentMode,
			handleDisplayFullwidth,
			sessionCOPChecks,
		} = this.props;
		const { isCOPCheckRequired, payeeId } = this.state;

		if (isCOPCheckRequired) {
			return (
				<COPCheck
					copCheckTriggered={copCheckTriggered}
					handleComplete={this.handleCOPSuccess}
					handleCancel={this.handleCOPCancel}
					handleEdit={this.handleCOPEdit}
					payeeId={payeeId}
					sessionCOPChecks={sessionCOPChecks}
				/>
			);
		}

		const { templateTitle, templateDescription, templateMakePaymentDescription, templateEditTitle, title } = content;

		const getPageTitle = () => {
			if (templateMode && templateId) return templateEditTitle;
			if (templateMode) return templateTitle;
			if (isAuthorisationEdit) return `Edit ${paymentName}`;
			return title;
		};

		const getPageDescription = () => {
			if (templateMode) return templateDescription;
			if (templateId) return templateMakePaymentDescription;
			return content.description;
		};
		const header = getPageTitle();
		const description = getPageDescription();
		const toAccountFilter = (accountId) => (account) =>
			!account.actionsAvailable[CAPABILITIES.IFT] && account.id !== accountId;

		return (
			<PayeesPage
				toPayees={toPayees}
				header={header}
				description={description}
				content={content}
				paymentCount={paymentCount}
				toAccount={toAccount}
				onPayeeListContinue={this.handlePayeeListContinue}
				updateFromAccount={updateFromAccount}
				showAccounts
				toAccountFilter={toAccountFilter(fromAccount)}
				fromAccounts={fromAccounts}
				paymentMode={paymentMode}
				paymentLimit={PAYMENTS_LIMIT}
				handleDisplayFullwidth={handleDisplayFullwidth}
			/>
		);
	}
}

BacsPayeePage.propTypes = {
	content: PropTypes.shape({}).isRequired,
	toPayees: PropTypes.arrayOf(PropTypes.shape(payeePropTypes)).isRequired,
	continueToPayments: PropTypes.func.isRequired,
	copCheckTriggered: PropTypes.func,
	addMultiplePayments: PropTypes.func.isRequired,
	selectedPaymentId: PropTypes.string,
	clearSelectedPayment: PropTypes.func.isRequired,
	paymentCount: PropTypes.number.isRequired,
	updateFromAccount: PropTypes.func.isRequired,
	toAccount: PropTypes.string,
	handleSelectToAccount: PropTypes.func,
	isAuthorisationEdit: PropTypes.bool.isRequired,
	templateMode: PropTypes.bool.isRequired,
	templateId: PropTypes.string,
	paymentName: PropTypes.string,
	fromAccount: PropTypes.string,
	fromAccounts: PropTypes.shape({}),
	paymentMode: PropTypes.string,
	lastPayment: PropTypes.object.isRequired,
	handleDisplayFullWidth: PropTypes.func,
	sessionCOPChecks: PropTypes.object,
};
export default compose(
	withScrollTop,
	withContent({ content: pageContent }),
	connect(mapState, mapDispatch)
)(BacsPayeePage);
